<script setup lang="ts">
import moment from 'moment'
import BackButton from '~/components/global/BackButton.vue'
import Icon from '~/components/global/Icon.vue'
import Container from '~/components/layout/Container.vue'
import type { Article } from '~/types/api/Article'

const { t } = useI18n()
const router = useRouter()
const props = defineProps<{
  article: Article
}>()

useHead({
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: props.article.image,
    },
  ],
})

const firstTagName = computed(() => {
  if (!props.article.tags.length) {
    return null
  }

  return props.article.tags[0].label
})

const share = useShare({
  title: props.article.name,
  text: props.article.name,
})

const lastRoutePath = computed(() => {
  let back = router.options.history.state.back

  if (back) {
    back = back.toString().split('?')[0].split('#')[0]
  }

  return back
})

const listingLocaleLink = useLocaleLink({
  name: 'news',
})

const backText = computed(() => {
  if (lastRoutePath.value === listingLocaleLink?.path) {
    return t(`pages.news.detail.header.back.long`)
  }

  return null
})

const backTextShort = computed(() => {
  if (lastRoutePath.value === listingLocaleLink?.path) {
    return t(`pages.news.detail.header.back.short`)
  }

  return null
})

const imageData = getImageSrcSizesData(props.article.image, {
  preload: true,
  defaultFormat: '900x1800',
  sizes: '(max-width: 991.99px) 95vw, 900px',
})
</script>

<template>
  <div class="c-news-detail-header">
    <Container class="c-news-detail-header__container" variant="narrow">
      <div class="c-news-detail-header__back">
        <BackButton :back-text="backText" :back-text-short="backTextShort" />
      </div>

      <div v-if="firstTagName" class="c-news-detail-header__hashtag">
        #{{ firstTagName }}
      </div>

      <div class="c-news-detail-header__info">
        <span
          v-if="article.show_publish_date && article.publish_date"
          class="sc-item"
          >{{ moment(article.publish_date).format('DD.MM.YYYY') }}</span
        >

        <span class="sc-item">{{
          $t(
            'pages.news.readingTime',
            { _0: article.reading_time },
            article.reading_time
          )
        }}</span>
      </div>

      <h1 class="c-news-detail-header__title">{{ article.name }}</h1>

      <div class="c-news-detail-header__image">
        <IButton
          v-if="share.canShare.value"
          color="primary"
          @click="share.share"
        >
          <Icon name="share" />
        </IButton>

        <img
          :src="imageData.src"
          :srcset="imageData.srcset"
          :sizes="imageData.sizes"
          :alt="article.name"
        />
      </div>
    </Container>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-news-detail-header {
  padding-top: 48px;

  @include breakpoint-down('md') {
    padding-top: 32px;
  }

  &__container {
    display: grid;
    grid-template:
      'back back' auto
      'hashtag info' auto
      'title title' auto
      'image image' auto
      / 1fr auto;
    gap: 0 24px;
    align-items: center;

    @include breakpoint-down('md') {
      grid-template:
        'back back' auto
        'title title' auto
        'hashtag info' auto
        'image image' auto
        / 1fr auto;
    }

    @include breakpoint-down('xs') {
      grid-template:
        'back' auto
        'title' auto
        'hashtag' auto
        'info' auto
        'image' auto
        / 1fr;
    }
  }

  &__back {
    grid-area: back;
    margin-bottom: 60px;
    justify-self: start;

    @include breakpoint-down('md') {
      margin-bottom: 32px;
    }

    @include breakpoint-down('xs') {
      margin-bottom: 28px;
    }
  }

  &__hashtag {
    grid-area: hashtag;
    font-size: 18px;
    font-weight: 500;
    color: var(--color-secondary);

    @include breakpoint-down('sm') {
      font-size: 16px;
    }

    @include breakpoint-down('xs') {
      margin-bottom: 6px;
    }
  }

  &__info {
    grid-area: info;
    display: flex;
    gap: 8px;

    span {
      color: var(--color-solid-grey-dark);
      font-size: 18px;
      line-height: 1;

      @include breakpoint-down('sm') {
        font-size: 16px;
      }

      &:not(:last-child) {
        padding-right: 8px;
        border-right: 1px solid var(--color-border);
      }
    }
  }

  &__title {
    grid-area: title;
    margin-top: 24px;
    margin-bottom: 32px;
    font-size: 58px;
    line-height: 0.96;

    @include breakpoint-down('md') {
      font-size: 48px;
      margin-top: 0;
      margin-bottom: 24px;
    }

    @include breakpoint-down('xs') {
      font-size: 36px;
      margin-bottom: 18px;
    }
  }

  &__image {
    grid-area: image;
    width: 100%;
    aspect-ratio: 3 / 2;
    background-color: var(--color-solid-grey);
    position: relative;

    @include breakpoint-down('md') {
      margin-top: 12px;
    }

    @include breakpoint-down('xs') {
      margin-top: 18px;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    :deep(.button) {
      --button--font-size: 22px;
      --button--padding-left: 11px;
      --button--padding-right: 13px;

      position: absolute;
      top: 8px;
      right: 8px;
      z-index: 2;
      min-height: 0;

      @include breakpoint-down('xs') {
        --button--font-size: 18px;
        --button--padding-top: 9px;
        --button--padding-bottom: 9px;
        --button--padding-left: 8px;
        --button--padding-right: 10px;
      }
    }
  }
}
</style>
